import { ArrowSquareOut } from '@phosphor-icons/react';
import { VText } from '@vake/ui-kit';
import { Feature, FeatureCollection } from 'geojson';
import { FC, useEffect, useState } from 'react';
import { DetectionImage, PlanetThumbnail, TargetOfInterestImage } from '../../../shared/components/Popup';
import { externalVesselUrl } from '../../../utils/mmsiExternalCall';
import { ImageTransform } from '../../../utils/transform';
import { IChosenBoatObject } from '../interactiveReport/PathfinderReport';
import { Arrow } from '../interactiveReport/reportInfo/ReportInfo';
import { PathfinderChosenImage } from './PathfinderChosenImage';

interface IDefiningBoat {
    image_path: string | null;
    datestring: string;
    time: string;
}

const GetBoatProperties = (item: IChosenBoatObject) => {
    const image_path = item?.image_path ? item.image_path : null;
    const date = item?.timestamp ? new Date(item?.timestamp).toUTCString().split(' ') : ' ';
    const datestring = date[0] + ' ' + date[2] + ' ' + date[1] + ' ' + date[3];
    const time = date[4] + ' UTC';
    const definingBoat: IDefiningBoat = { image_path: image_path, datestring: datestring, time: time };
    return definingBoat;
};

export const PathfinderList: FC<{
    aisPositions: FeatureCollection | undefined;
    chosenBoatObject: IChosenBoatObject | null;
    boatMetaData: Feature | null;
    setBoatMetaData: (value: Feature | null) => void;
    setHoveredPathElement: (value: number | null) => void;
    mmsi: string | null;
    reportName: string | null;
}> = ({ aisPositions, chosenBoatObject, boatMetaData, setBoatMetaData, setHoveredPathElement, mmsi, reportName }) => {
    const [positions, setPositions] = useState<Feature[]>([]);
    const [numCaptures, setNumCaptures] = useState<number | null>(null);
    const [chosenImage, setChosenImage] = useState<number | null>(null);
    const [chosenDate, setChosenDate] = useState<string | null>(null);
    const [chosenTime, setChosenTime] = useState<string | null>(null);
    const [startIndex, setStartIndex] = useState<number>(0);
    const [endIndex, setEndIndex] = useState<number>(-1);

    const buttonIncrement = 30;
    const listIncrement = 30;

    useEffect(() => {
        if (aisPositions) {
            setPositions(aisPositions.features);
            setNumCaptures(aisPositions.features.length);
            setEndIndex(aisPositions.features.length >= 30 ? 30 : aisPositions.features.length);
        }
    }, [aisPositions]);

    useEffect(() => {
        // If an AIS estimate popup/image is clicked in the map
        if (chosenBoatObject) {
            aisPositions?.features.forEach((item, index) => {
                // Looping through estimated AIS positions to find the index of the chosen ship (clicked popup/image)
                if (item.id === chosenBoatObject.id?.toString()) {
                    const definingBoat: IDefiningBoat = GetBoatProperties({ ...item.properties });
                    setBoatMetaData(positions[index]);
                    if (positions.length < 40) {
                        setStartIndex(0);
                        setEndIndex(positions.length);
                    } else {
                        setStartIndex(index);
                        setEndIndex(index + 30 <= positions?.length ? index + 30 : positions?.length);
                    }
                    setChosenImage(definingBoat.image_path);
                    setChosenDate(definingBoat.datestring);
                    setChosenTime(definingBoat.time);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chosenBoatObject]);

    return (
        <div className="absolute z-10 my-2 right-2 w-1/5 min-w-[175px] max-w-[375px] bg-background-primary">
            {!boatMetaData ? (
                <div>
                    <div className="flex flex-col h-[60vh] overflow-y-scroll">
                        <div className="flex gap-4">
                            {mmsi && (
                                <ArrowSquareOut
                                    className="cursor-pointer"
                                    size={20}
                                    onClick={() => window.open(externalVesselUrl(mmsi), '_blank')}
                                    color="white"
                                />
                            )}
                        </div>
                        <VText>{numCaptures ? numCaptures + ' satellite images' : '  '}</VText>
                        {positions.slice(startIndex, endIndex).map((item, index) => {
                            const definingBoat: IDefiningBoat = GetBoatProperties({ ...item.properties });
                            const isSentinel = item.properties?.image_source !== 'planet_scope'; // TODO: Needs to be updated if we add more sources
                            return (
                                <div
                                    className="flex items-center gap-2 border-2 border-background-primary hover:border-warning-default"
                                    key={index}
                                    onClick={() => {
                                        const newIndex = startIndex + index;
                                        setBoatMetaData(positions[newIndex]);
                                        if (positions.length < 40) {
                                            setStartIndex(0);
                                            setEndIndex(positions.length);
                                        } else if (
                                            positions.length > listIncrement &&
                                            positions.length > newIndex + listIncrement
                                        ) {
                                            setEndIndex(positions.length);
                                            setStartIndex(positions.length - listIncrement);
                                        } else {
                                            setStartIndex(newIndex - 1 >= 0 ? newIndex - 1 : 0);
                                            setEndIndex(
                                                newIndex + listIncrement <= positions?.length
                                                    ? newIndex + listIncrement
                                                    : positions?.length
                                            );
                                        }
                                        setChosenImage(definingBoat.image_path);
                                        setChosenDate(definingBoat.datestring);
                                        setChosenTime(definingBoat.time);
                                    }}
                                    onMouseEnter={() => setHoveredPathElement(startIndex + index)}
                                    onMouseLeave={() => setHoveredPathElement(null)}
                                >
                                    <div className="w-[120px] h-[120px] overflow-hidden">
                                        {isSentinel ? (
                                            <ImageTransform farNorthScaling={item.properties?.lat}>
                                                {definingBoat.image_path ? (
                                                    <DetectionImage image_path={definingBoat.image_path} />
                                                ) : (
                                                    <TargetOfInterestImage
                                                        imageSource={item.properties?.image_source}
                                                        lon={item.properties?.lon}
                                                        lat={item.properties?.lat}
                                                        timestamp={item.properties?.timestamp}
                                                        mmsi={item.properties?.mmsi}
                                                    />
                                                )}
                                            </ImageTransform>
                                        ) : (
                                            <PlanetThumbnail image_path={item.properties?.preview_url} />
                                        )}
                                    </div>
                                    <div className="flex flex-col flex-1 text-center items-center justify-center">
                                        <VText>{definingBoat.datestring}</VText>
                                        <VText>{definingBoat.time}</VText>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {numCaptures ? (
                        <>
                            <div className="flex justify-center p-2">
                                <VText>
                                    Images {startIndex + 1} to {endIndex}
                                </VText>
                            </div>
                            <div className="flex justify-center gap-2 p-2">
                                <Arrow
                                    disabled={startIndex === 0}
                                    onClick={() => {
                                        const newStartIndex =
                                            startIndex - buttonIncrement >= 0 ? startIndex - buttonIncrement : 0;
                                        const newEndIndex =
                                            newStartIndex <= 0
                                                ? buttonIncrement > positions.length
                                                    ? positions.length
                                                    : buttonIncrement
                                                : endIndex - buttonIncrement;

                                        setStartIndex(newStartIndex);
                                        setEndIndex(newEndIndex);
                                    }}
                                >
                                    <img src="/images/icons/ArrowLeft.svg" alt="Previous date" />
                                </Arrow>
                                <Arrow
                                    disabled={endIndex === positions.length}
                                    onClick={() => {
                                        const newEndIndex =
                                            endIndex + buttonIncrement <= numCaptures
                                                ? endIndex + buttonIncrement
                                                : numCaptures;
                                        const newStartIndex =
                                            newEndIndex >= numCaptures
                                                ? numCaptures - buttonIncrement < 0
                                                    ? 0
                                                    : numCaptures - buttonIncrement
                                                : startIndex + buttonIncrement;

                                        setStartIndex(newStartIndex);
                                        setEndIndex(newEndIndex);
                                    }}
                                >
                                    <img src="/images/icons/ArrowRight.svg" alt="arrow" />
                                </Arrow>
                            </div>
                        </>
                    ) : null}
                </div>
            ) : (
                <PathfinderChosenImage
                    boatMetaData={boatMetaData}
                    chosenImage={chosenImage}
                    chosenDate={chosenDate}
                    chosenTime={chosenTime}
                    setBoatMetaData={setBoatMetaData}
                    setChosenImage={setChosenImage}
                    reportName={reportName}
                />
            )}
        </div>
    );
};
